import { Calendar, ChevronDown, RotateCcw, Search, SlidersHorizontal, X } from "lucide-react";
import { useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import Modal from "@/components/ui/Modal";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Select from "@/components/ui/Select";
import { DEFAULT_RANGE, RANGES } from "@/pages/retailer/installments/data";

interface Props {
  search: string;
  onSearch: (v: string) => void;
  retailer: string;
  onRetailer: (v: string) => void;
  bank: string;
  onBank: (v: string) => void;
  range: string;
  onRange: (v: string) => void;
  rangeOpen: boolean;
  onRangeOpen: (v: boolean) => void;
  customFrom: string;
  onCustomFrom: (v: string) => void;
  customTo: string;
  onCustomTo: (v: string) => void;
  onReset: () => void;
  retailerOptions: { value: string; label: string }[];
  bankOptions: { value: string; label: string }[];
}

export default function FilterBar(props: Props) {
  const {
    search,
    onSearch,
    retailer,
    onRetailer,
    bank,
    onBank,
    range,
    onRange,
    rangeOpen,
    onRangeOpen,
    customFrom,
    onCustomFrom,
    customTo,
    onCustomTo,
    onReset,
    retailerOptions,
    bankOptions,
  } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileRangeOpen, setMobileRangeOpen] = useState(false);

  const searchableRetailerOptions = useMemo(
    () => retailerOptions.filter((o) => Boolean(o.value)),
    [retailerOptions]
  );

  const chips: { label: string; clear: () => void }[] = [];
  if (search) chips.push({ label: `Search: ${search}`, clear: () => onSearch("") });
  if (retailer) chips.push({ label: retailerOptions.find((o) => o.value === retailer)?.label ?? retailer, clear: () => onRetailer("") });
  if (bank) chips.push({ label: bank, clear: () => onBank("") });
  if (range === "Custom Range") {
    if (customFrom || customTo) {
      chips.push({
        label: `${customFrom || "Any"} to ${customTo || "Any"}`,
        clear: () => {
          onCustomFrom("");
          onCustomTo("");
          onRange(DEFAULT_RANGE);
        },
      });
    }
  } else if (range !== DEFAULT_RANGE) {
    chips.push({ label: range, clear: () => onRange(DEFAULT_RANGE) });
  }

  const hasFilters = chips.length > 0;

  const searchField = (
    <div className="relative flex-1 min-w-[240px]">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-ink-muted">
        <Search className="h-4 w-4" />
      </span>
      <input
        type="text"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search installment, mandate, customer, UTR..."
        className="h-[42px] w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:border-primary-500 focus:ring-primary-500/30"
      />
    </div>
  );

  const rangeField = (
    <div className="relative w-full sm:w-44">
      <button
        type="button"
        onClick={() => (mobileOpen ? setMobileRangeOpen((v) => !v) : onRangeOpen(!rangeOpen))}
        className="flex h-[42px] w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3.5 text-sm text-ink hover:bg-slate-50 cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-ink-muted" />
          {range}
        </span>
        <ChevronDown className="h-4 w-4 text-ink-muted" />
      </button>
      {((!mobileOpen && rangeOpen) || (mobileOpen && mobileRangeOpen)) && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => (mobileOpen ? setMobileRangeOpen(false) : onRangeOpen(false))} />
          <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  onRange(r);
                  if (mobileOpen) setMobileRangeOpen(false);
                  else onRangeOpen(false);
                }}
                className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                  range === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="rounded-[10px] border border-line bg-card p-4 shadow-card">
      <div className="hidden flex-col gap-3 lg:flex">
        <div className="flex flex-wrap items-center gap-3">
          {searchField}
          <div className="w-full sm:w-56">
            <SearchableSelect
              value={retailer}
              onChange={onRetailer}
              options={searchableRetailerOptions}
              placeholder="All Retailers"
            />
          </div>
          <div className="w-full sm:w-44">
            <Select value={bank} onChange={(e) => onBank(e.target.value)} options={bankOptions} placeholder="All Banks" />
          </div>
          {rangeField}
          {range === "Custom Range" && (
            <div className="flex items-center gap-2">
              <div className="w-36">
                <DatePicker value={customFrom} onChange={onCustomFrom} placeholder="From date" />
              </div>
              <span className="text-xs text-ink-muted">to</span>
              <div className="w-36">
                <DatePicker value={customTo} onChange={onCustomTo} placeholder="To date" />
              </div>
            </div>
          )}
          <Button variant="ghost" onClick={onReset} leftIcon={<RotateCcw className="h-4 w-4" />}>
            Reset
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 lg:hidden">
        {searchField}
        <Button variant="secondary" leftIcon={<SlidersHorizontal className="h-4 w-4" />} onClick={() => setMobileOpen(true)}>
          Filters
        </Button>
      </div>

      {hasFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {chips.map((c) => (
            <span
              key={c.label}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-50 px-3 py-1 text-[13px] font-medium text-primary-600"
            >
              {c.label}
              <button
                type="button"
                onClick={c.clear}
                className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-100 cursor-pointer"
                aria-label={`Remove filter ${c.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Modal open={mobileOpen} onClose={() => setMobileOpen(false)} title="Filters">
        <div className="flex flex-col gap-4">
          <Select value={retailer} onChange={(e) => onRetailer(e.target.value)} options={retailerOptions} placeholder="All Retailers" />
          <Select value={bank} onChange={(e) => onBank(e.target.value)} options={bankOptions} placeholder="All Banks" />
          {rangeField}
          {range === "Custom Range" && (
            <div className="flex flex-col gap-3">
              <DatePicker label="From Date" value={customFrom} onChange={onCustomFrom} placeholder="From date" />
              <DatePicker label="To Date" value={customTo} onChange={onCustomTo} placeholder="To date" />
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}